import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Paper,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { 
  TrendingUp,
  Person,
  AttachMoney,
  Casino,
  DateRange,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { galiAPI } from '../../../services/api';

const StatCard = ({ title, value, icon, color }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
  >
    <Card
      sx={{
        background: `linear-gradient(135deg, ${color}20 0%, ${color}10 100%)`,
        border: `1px solid ${color}30`,
        borderRadius: 3,
      }}
    >
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography color="text.secondary" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" component="div" sx={{ color, fontWeight: 'bold' }}>
              {value}
            </Typography>
          </Box>
          <Box sx={{ color: color }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  </motion.div>
);

// Row component for expandable rows
function OrderRow(props) {
  const { row } = props;
  const [open, setOpen] = useState(false);

  // Safely access properties with defaults
  const bets = row.bets || [];
  
  // Calculate summary if not provided
  const summary = row.summary || {
    total_bets: bets.length,
    pending: bets.filter(b => b.status === 'pending').length,
    win: bets.filter(b => b.status === 'win').length,
    loss: bets.filter(b => b.status === 'loss').length,
    cancelled: bets.filter(b => b.status === 'cancelled').length,
    total_win_amount: bets
      .filter(b => b.status === 'win')
      .reduce((sum, bet) => sum + parseFloat(bet.win_amount || 0), 0)
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'win': return 'success';
      case 'loss': return 'error';
      case 'cancelled': return 'default';
      default: return 'default';
    }
  };

  const getGameModeColor = (gameMode) => {
    switch (gameMode) {
      case 'JANTRI': return '#6366f1';
      case 'CROSS': return '#10b981';
      case 'OPEN': return '#f59e0b';
      default: return '#6366f1';
    }
  };

  return (
    <>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row">
          {row.order_id}
        </TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight="500">
            {row.username || 'N/A'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            ID: {row.user_id}
          </Typography>
        </TableCell>
        <TableCell>{new Date(row.placed_at || row.createdAt).toLocaleString()}</TableCell>
        <TableCell>{row.market_name}</TableCell>
        <TableCell>
          <Chip
            label={row.game_mode}
            sx={{
              bgcolor: getGameModeColor(row.game_mode),
              color: 'white',
              fontWeight: 'bold'
            }}
            size="small"
          />
        </TableCell>
        <TableCell align="right">₹{row.total_amount || 0}</TableCell>
        <TableCell>
          <Chip
            label={row.status || 'unknown'}
            color={getStatusColor(row.status)}
            variant="filled"
            size="small"
          />
        </TableCell>
        <TableCell align="right">
          <Typography>
            {summary.total_bets} Bets
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Box display="flex" flexDirection="column">
            <Typography variant="caption" color="success.main">
              Win: {summary.win || 0}
            </Typography>
            <Typography variant="caption" color="error.main">
              Loss: {summary.loss || 0}
            </Typography>
            {(summary.pending > 0) && (
              <Typography variant="caption" color="warning.main">
                Pending: {summary.pending}
              </Typography>
            )}
          </Box>
        </TableCell>
        <TableCell align="right">
          <Typography color="success.main" fontWeight="bold">
            ₹{(summary.total_win_amount || 0).toFixed(2)}
          </Typography>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={11}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Typography variant="h6" gutterBottom component="div">
                Bet Details
              </Typography>
              <Table size="small" aria-label="bets">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Number</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Win Amount</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bets.map((bet) => (
                    <TableRow key={bet.id}>
                      <TableCell component="th" scope="row">
                        {bet.id}
                      </TableCell>
                      <TableCell>{bet.bet_category || '-'}</TableCell>
                      <TableCell>{bet.generated_value || '-'}</TableCell>
                      <TableCell align="right">₹{bet.bet_amount || 0}</TableCell>
                      <TableCell align="right">
                        {bet.status === 'win' ? `₹${bet.win_amount || 0}` : '-'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={bet.status || 'unknown'}
                          color={getStatusColor(bet.status)}
                          variant="filled"
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

const GaliBets = () => {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    win: 0,
    loss: 0,
    cancelled: 0
  });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    date: '',
    market_id: '',
    status: '',
    game_mode: ''
  });
  const [markets, setMarkets] = useState([]);

  useEffect(() => {
    fetchBets();
    fetchMarkets();
  }, []);

  const fetchMarkets = async () => {
    try {
      const response = await galiAPI.getGaliMarkets();
      setMarkets(response.data.markets || []);
    } catch (error) {
      console.error("Failed to fetch markets", error);
    }
  };

  const fetchBets = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.date) params.date = filters.date;
      if (filters.market_id) params.market_id = filters.market_id;
      if (filters.status) params.status = filters.status;
      if (filters.game_mode) params.game_mode = filters.game_mode;
      
      const response = await galiAPI.getGaliBets(params);
      console.log("API Response:", response.data); // Debug log
      
      // Handle different possible response structures
      if (response.data.orders) {
        setOrders(response.data.orders);
      } else if (response.data.bets) {
        // If old API format, transform to new structure
        const groupedBets = {};
        response.data.bets.forEach(bet => {
          const orderId = bet.order_id || 'no-order';
          if (!groupedBets[orderId]) {
            groupedBets[orderId] = {
              order_id: bet.order_id || 'no-order',
              market_id: bet.market_id,
              market_name: bet.market?.market_name || 'Unknown Market',
              game_mode: bet.game_mode,
              total_amount: 0,
              status: 'active',
              placed_at: bet.placed_at || bet.createdAt,
              bets: []
            };
          }
          groupedBets[orderId].bets.push(bet);
          groupedBets[orderId].total_amount += parseFloat(bet.bet_amount || 0);
        });
        
        setOrders(Object.values(groupedBets));
      } else {
        setOrders([]);
      }
      
      // Set stats directly if provided
      if (response.data.stats) {
        setStats(response.data.stats);
      } else {
        // Calculate stats from bets if not provided
        const calculatedStats = {
          total: 0,
          pending: 0,
          win: 0,
          loss: 0,
          cancelled: 0
        };
        
        const allBets = [];
        if (response.data.bets) {
          allBets.push(...response.data.bets);
        } else if (response.data.orders) {
          response.data.orders.forEach(order => {
            if (order.bets) allBets.push(...order.bets);
          });
        }
        
        calculatedStats.total = allBets.length;
        calculatedStats.pending = allBets.filter(b => b.status === 'pending').length;
        calculatedStats.win = allBets.filter(b => b.status === 'win').length;
        calculatedStats.loss = allBets.filter(b => b.status === 'loss').length;
        calculatedStats.cancelled = allBets.filter(b => b.status === 'cancelled').length;
        
        setStats(calculatedStats);
      }
    } catch (error) {
      console.error("API Error:", error); // Debug log
      toast.error(error.response?.data?.message || 'Failed to fetch bets');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    fetchBets();
  };

  const clearFilters = () => {
    setFilters({
      date: '',
      market_id: '',
      status: '',
      game_mode: ''
    });
    // Fetch without filters
    setTimeout(fetchBets, 0);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Gali Bet Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Monitor and manage all Gali betting activities
          </Typography>
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Typography variant="h6" gutterBottom>
          Search Filters
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Date"
              type="date"
              value={filters.date}
              onChange={(e) => setFilters({...filters, date: e.target.value})}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth sx={{ minWidth: 200 }}>
              <InputLabel shrink={!!filters.market_id || filters.market_id === ''}>
                Market
              </InputLabel>
              <Select
                value={filters.market_id}
                onChange={(e) => setFilters({...filters, market_id: e.target.value})}
                label="Market"
                displayEmpty
                notched={!!filters.market_id || filters.market_id === ''}
                sx={{ minHeight: 56 }}
                renderValue={(selected) => {
                  if (!selected) {
                    return <em style={{ color: '#999', fontStyle: 'normal' }}>All Markets</em>;
                  }
                  const market = markets.find(m => m.id === selected);
                  return market?.market_name || 'All Markets';
                }}
              >
                <MenuItem value="">All Markets</MenuItem>
                {markets.map(market => (
                  <MenuItem key={market.id} value={market.id}>
                    {market.market_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth sx={{ minWidth: 200 }}>
              <InputLabel shrink={!!filters.game_mode || filters.game_mode === ''}>
                Game Mode
              </InputLabel>
              <Select
                value={filters.game_mode}
                onChange={(e) => setFilters({...filters, game_mode: e.target.value})}
                label="Game Mode"
                displayEmpty
                notched={!!filters.game_mode || filters.game_mode === ''}
                sx={{ minHeight: 56 }}
                renderValue={(selected) => {
                  if (!selected) {
                    return <em style={{ color: '#999', fontStyle: 'normal' }}>All Modes</em>;
                  }
                  return selected;
                }}
              >
                <MenuItem value="">All Modes</MenuItem>
                <MenuItem value="JANTRI">JANTRI</MenuItem>
                <MenuItem value="CROSS">CROSS</MenuItem>
                <MenuItem value="OPEN">OPEN</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth sx={{ minWidth: 200 }}>
              <InputLabel shrink={!!filters.status || filters.status === ''}>
                Status
              </InputLabel>
              <Select
                value={filters.status}
                onChange={(e) => setFilters({...filters, status: e.target.value})}
                label="Status"
                displayEmpty
                notched={!!filters.status || filters.status === ''}
                sx={{ minHeight: 56 }}
                renderValue={(selected) => {
                  if (!selected) {
                    return <em style={{ color: '#999', fontStyle: 'normal' }}>All</em>;
                  }
                  return selected.charAt(0).toUpperCase() + selected.slice(1);
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="win">Win</MenuItem>
                <MenuItem value="loss">Loss</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Button
              variant="outlined"
              onClick={clearFilters}
              fullWidth
            >
              Clear Filters
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Button
              variant="contained"
              onClick={applyFilters}
              fullWidth
            >
              Apply Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Statistics Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Bets"
            value={stats.total}
            icon={<Casino sx={{ fontSize: 40 }} />}
            color="#6366f1"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Pending"
            value={stats.pending}
            icon={<DateRange sx={{ fontSize: 40 }} />}
            color="#f59e0b"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Win"
            value={stats.win}
            icon={<TrendingUp sx={{ fontSize: 40 }} />}
            color="#10b981"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Loss"
            value={stats.loss}
            icon={<Person sx={{ fontSize: 40 }} />}
            color="#ef4444"
          />
        </Grid>
      </Grid>

      {/* Orders Table with Expandable Rows */}
      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Bet Orders ({orders.length})
          </Typography>
          {orders.length > 0 ? (
            <TableContainer>
              <Table aria-label="collapsible table">
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell>Order ID</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Date & Time</TableCell>
                    <TableCell>Market</TableCell>
                    <TableCell>Game Mode</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Bets</TableCell>
                    <TableCell align="right">Results</TableCell>
                    <TableCell align="right">Win Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orders.map((order) => (
                    <OrderRow key={order.order_id} row={order} />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box py={3} textAlign="center">
              <Typography variant="body1" color="text.secondary">
                No bet orders found matching your criteria
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default GaliBets;